import { useCallback } from 'react';

/** Purpose: Provides stable record/permission mapper helpers. Inputs: none. Output: memoized mapping utilities. */
export default function useRecordTransforms() {
  const mapRecordType = useCallback((frontendType) => {
    const mapping = {
      'Lab Results': 'LAB_RESULTS',
      'Imaging': 'IMAGING',
      'Prescriptions': 'PRESCRIPTIONS',
      'Consultation Notes': 'CONSULTATION',
      'Immunizations': 'IMMUNIZATIONS',
      'Surgical Reports': 'SURGICAL',
      'Discharge Summary': 'DISCHARGE',
      'Other': 'OTHER'
    };
    return mapping[frontendType] || 'OTHER';
  }, []);

  const mapRecordTypeToFrontend = useCallback((backendType) => {
    const mapping = {
      LAB_RESULTS: 'Lab Results',
      IMAGING: 'Imaging',
      PRESCRIPTIONS: 'Prescriptions',
      CONSULTATION: 'Consultation Notes',
      IMMUNIZATIONS: 'Immunizations',
      SURGICAL: 'Surgical Reports',
      DISCHARGE: 'Discharge Summary',
      OTHER: 'Other'
    };
    return mapping[backendType] || backendType;
  }, []);

  const normalizeAccessLevel = useCallback((value) => {
    if (!value) return 'READ';
    const normalized = String(value).toUpperCase();
    if (normalized === 'READ') return 'READ';
    return 'READ';
  }, []);

  const transformRecord = useCallback((record) => ({
    id: record.id,
    title: record.title,
    type: mapRecordTypeToFrontend(record.recordType),
    provider: record.provider || '',
    facility: record.facility || '',
    date: record.recordDate,
    description: record.description || '',
    tags: record.tags || [],
    fileName: record.fileName || '',
    uploadDate: record.createdAt ? record.createdAt.split('T')[0] : '',
    encrypted: record.encrypted
  }), [mapRecordTypeToFrontend]);

  const transformPermission = useCallback((perm) => ({
    id: perm.id,
    grantedTo: perm.grantedTo,
    organization: perm.organization,
    purpose: perm.purpose,
    recordIds: perm.recordIds || [],
    records: perm.records || [],
    grantedDate: perm.grantedDate,
    expiryDate: perm.expiryDate,
    accessLevel: perm.accessLevel,
    accessCount: perm.accessCount,
    provider: perm.provider || null,
    daysUntilExpiry: perm.daysUntilExpiry ?? null,
    isExpiringSoon: perm.isExpiringSoon ?? false,
    status: (perm.status || 'unknown').toLowerCase()
  }), []);

  return {
    mapRecordType,
    normalizeAccessLevel,
    transformRecord,
    transformPermission
  };
}
